import { useCallback, useMemo } from 'react';
import { FilterByForm, FilterField, FilterOption } from 'models/Filters';
import useQueryParams from 'hooks/useQueryParams';
import utils, {
  getResetedPageParams,
  isValueEmpty,
  getHasElementsInArray,
} from 'utils';
import { PAGE_PARAM_KEYWORD } from 'data/global';
import { INITIAL_VALUE } from '../components/MultiFilterOptions/MultiFilterOptions.component';

export const getFilterParams = (
  filterValues: FilterField[],
  filterOptions: FilterOption[],
) =>
  filterValues
    .filter(({ name, value }) => name && !isValueEmpty(value))
    .reduce((acc, { name, value }) => {
      const filter = filterOptions.find((option) => option.value === name);

      if (filter?.apply) return { ...acc, ...filter.apply(value) };

      return {
        ...acc,
        [name]: Array.isArray(value)
          ? utils.mapArrayToParams(
              utils.multiSelectValueToStringArray(value as any),
            )
          : value,
      };
    }, {} as Record<string, unknown>);

type UseMultiFilterFormProps = {
  filters: FilterOption[];
  pageParam?: string | string[];
  onDone?: () => void;
};

export default function useMultiFilterForm(props: UseMultiFilterFormProps) {
  const { filters, pageParam = PAGE_PARAM_KEYWORD, onDone } = props;

  const { params, replaceMultipleQueryParams } = useQueryParams();

  const allFilters = useMemo(
    () =>
      filters.flatMap(({ value, clearFields }) =>
        getHasElementsInArray(clearFields) ? [...clearFields, value] : [value],
      ),
    [filters],
  );

  const hasActiveFilter = useMemo(
    () => allFilters.find((value) => params[value]),
    [allFilters, params],
  );

  const initialValues = useMemo(
    () => ({
      filters: hasActiveFilter
        ? filters
            .filter(({ value }) => params[value])
            .map(({ value, isMultiple, getInitialValue }) => ({
              name: value,
              value: getInitialValue
                ? getInitialValue?.(params)
                : isMultiple
                ? utils.mapParamsToArray(params[value])
                : params[value],
            }))
        : [INITIAL_VALUE],
    }),
    [filters, hasActiveFilter, params],
  );

  const onSubmit = useCallback(
    ({ filters: filterValues }: FilterByForm) => {
      const rawParams = getFilterParams(filterValues, filters);

      const filterParams = Object.fromEntries(
        Object.entries(rawParams).map(([name, value]) => [
          name,
          Array.isArray(value)
            ? utils.mapArrayToParams(
                utils.multiSelectValueToStringArray(value as any),
              )
            : value,
        ]),
      );

      const pageParams = getResetedPageParams(pageParam);

      const nonFilterParams = Object.fromEntries(
        Object.entries(params).filter(([key]) => !allFilters.includes(key)),
      );

      replaceMultipleQueryParams({
        ...nonFilterParams, // sort, limit and params that are not managed by this modal
        ...filterParams, // filters in modal
        ...pageParams, // reset to first page
      });

      onDone?.();
    },
    [
      allFilters,
      filters,
      pageParam,
      params,
      onDone,
      replaceMultipleQueryParams,
    ],
  );

  return { allFilters, initialValues, onSubmit };
}
